import React, {useContext, useEffect, useState, useRef, useCallback, useMemo, SetStateAction} from "react";
import { Admin, Agency, Blog, Car, Cost, DailyCost, Driver, FullReservation, GlobalUser, Guider, Passenger, Payment, Reservation, ReservationPayment, ReservationPaymentList, Seller, Tour } from "../types/common.types";
import AdminService from "../services/admin.service";
import { startOfToday } from 'date-fns'
import { AdminContextType } from "../types/admin";
import { useNavigate, useSearchParams } from "react-router-dom";
import toQuery from "../utils/toQuery";

const AdminProvider = React.createContext<AdminContextType>(null as any)

export const AdminContext = ({children}: {children: React.ReactNode}) => {
    const router = useNavigate()
    const [searchParams] = useSearchParams()

    const getUser = () => {
        const user = localStorage.getItem('admin')
        if (user) {
            return JSON.parse(user)
        } else return null
    }
    
    // Refs to track update sources
    const isUpdatingFromUrl = useRef(false);
    const initialLoadDone = useRef(false);
    const prevSearchParamsString = useRef(searchParams.toString());

    // useStates
    // superadmin
    const [isCreateAgencyModalOpen, setIsCreateAgencyModalOpen] = useState(false)
    const [editTourData, setEditTourData] = useState<Partial<Tour>>()
    const [confirmDeleteTourId, setConfirmDeleteTourId] = useState<string>()
    const [confirmDeleteAgencyId, setConfirmDeleteAgencyId] = useState<string>()
    
    // general
    const [user, setUser] = useState<GlobalUser>(getUser())
    
    // Check if user is superowner
    const isSuperOwner = user?.user?.source === 'superowners';
    
    // Initialize tab from URL - always allow tab to be read from URL and synced
    const [tab, setTab] = useState<string | undefined>(() => {
        return searchParams.get('tab') || 'tours';
    });
    
    // admin
    const [isCreateTourModalOpen, setIsCreateTourModalOpen] = useState(false)
    const [tourInfoId, setTourInfoId] = useState<string>()
    
    // create modals
    const [isCreatePassengerModalOpen, setIsCreatePassengerModalOpen] = useState(false)
    const [isCreateSellerModalOpen, setIsCreateSellerModalOpen] = useState(false)
    const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false)
    const [isCreateDriverModalOpen, setIsCreateDriverModalOpen] = useState(false)
    const [isCreateGuiderModalOpen, setIsCreateGuiderModalOpen] = useState(false)
    const [isCreateCarModalOpen, setIsCreateCarModalOpen] = useState(false)
    const [isCreateZoneModalOpen, setIsCreateZoneModalOpen] = useState<boolean>(false)
    const [isCreateCostModalOpen, setIsCreateCostModalOpen] = useState<boolean>(false)
    const [isCreatePaymentReservationModalOpen, setIsCreatePaymentReservationModalOpen] = useState<boolean>(false)
    const [isCreateReTraspasoModalOpen, setIsCreateReTraspasoModalOpen] = useState<boolean>(false)
    const [createPaymentReTraspasoData, setCreatePaymentReTraspasoData] = useState<any>(undefined)
    const [createRevisionData, setCreateRevisionData] = useState<any>(undefined)
    const [relationshipRefreshKey, setRelationshipRefreshKey] = useState<number>(0)
    const [createPaymentResId, setCreatePaymentResId] = useState<string>()
    const [createPaymentGroupId, setCreatePaymentGroupId] = useState<string>()
    const [isCreateDailyCostModalOpen, setIsCreateDailyCostModalOpen] = useState<boolean>(false)
    const [createPaymentReservationId, setCreatePaymentReservationId] = useState<string>()
    
    // list
    const [isListDailyCostsModalOpen, setIsListDailyCostsModalOpen] = useState<boolean>(false)
    const [viewPaymentAgencyId, setViewPaymentAgencyId] = useState<string>()
    const [relationshipAgencyId, setRelationshipAgencyId] = useState<string>()
    const [viewPaymentsData, setViewPaymentsData] = useState<ReservationPayment[]>()
    const [viewPaymentTraspassData, setViewPaymentTraspassData] = useState<Payment[]>()
    const [reservationDetails, setReservationDetails] = useState<FullReservation[]>()
    const [reservationData, setReservationData] = useState<ReservationPaymentList>()
    
    // assign / register
    const [passengerPlanId, setPassengerPlanId] = useState<string>()
    const [assignTripReservationId, setAssignTripReservationId] = useState<string[]>([])
    const [assignTripTourId, setAssignTripTourId] = useState<string>()
    const [traspassTripReservationId, setTraspassTripReservationId] = useState<string[]>([])
    const [traspassTripTourId, setTraspassTripTourId] = useState<string>()
    const [registerPassengersTripId, setRegisterPassengersTripId] = useState<string>()
    const [registerCostsTripId, setRegisterCostsTripId] = useState<string>()
    const [requirementsTripId, setRequirementsTripId] = useState<string>()
    const [accountingSellerId, setAccountingSellerId] = useState<string>()
    
    // edit
    const [editTripId, setEditTripId] = useState<string>()
    const [editReservationId, setEditReservationId] = useState<string>()
    const [editPassengerReservationId, setEditPassengerReservationId] = useState<string>()
    const [editPassengerData, setEditPassengerData] = useState<Partial<Passenger>>()
    const [editDriverData, setEditDriverData] = useState<Partial<Driver>>()
    const [editCarData, setEditCarData] = useState<Partial<Car>>()
    const [editGuiderData, setEditGuiderData] = useState<Partial<Guider>>()
    const [editSellerData, setEditSellerData] = useState<Partial<Seller>>()
    const [editAdminData, setEditAdminData] = useState<Partial<Admin>>()
    const [editAgencyData, setEditAgencyData] = useState<Partial<Agency & { password: string }>>()
    const [editCostData, setEditCostData] = useState<Partial<Cost>>()
    const [addPaymentData, setAddPaymentData] = useState<Partial<Payment>>()
    const [editTourInfoId, setEditTourInfoId] = useState<string>()
    const [createBlogTourId, setCreateBlogTourId] = useState<string>()
    const [editZoneId, setEditZoneId] = useState<string>()
    const [groupDetails, setGroupDetails] = useState<Reservation[]>()
    const [listBlogsTourId, setListBlogsTourId] = useState<string>()
    const [editBlog, setEditBlog] = useState<Blog>()
    const [editPaymentData, setEditPaymentData] = useState<Payment>()
    const [editPaymentReservationData, setEditPaymentReservationData] = useState<ReservationPayment>()
    const [editDailyCostData, setEditDailyCostData] = useState<DailyCost>()
    
    // confirm delete
    const [confirmDeleteCarId, setConfirmDeleteCarId] = useState<string>()
    const [confirmDeleteTripId, setConfirmDeleteTripId] = useState<string>()
    const [confirmDeleteReservationId, setConfirmDeleteReservationId] = useState<string>()
    const [confirmUnassignReservationId, setConfirmUnassignReservationId] = useState<string>()
    const [confirmDeleteCostId, setConfirmDeleteCostId] = useState<string>()
    const [confirmDeletePaymentId, setConfirmDeletePaymentId] = useState<string>()
    const [confirmDeletePassengerId, setConfirmDeletePassengerId] = useState<string>()
    
    // Date states - Initialize from URL with proper parsing
    const [currentDay, setCurrentDay] = useState<Date>(() => {
        // For superowners, always use today's date without reading from URL
        if (isSuperOwner) {
            console.log('Superowner: using today as default for currentDay');
            return startOfToday();
        }
        
        const urlDay = searchParams.get('currentDay');
        console.log('Initializing currentDay from URL:', urlDay);
        
        if (urlDay) {
            try {
                const date = new Date(urlDay);
                if (!isNaN(date.getTime())) {
                    console.log('Parsed currentDay:', date);
                    return date;
                }
            } catch (e) {
                console.error('Error parsing currentDay:', e);
            }
        }
        
        console.log('Using today as fallback for currentDay');
        return startOfToday();
    });

    const [endDay, setEndDay] = useState<Date | undefined>(() => {
        // For superowners, always use undefined without reading from URL
        if (isSuperOwner) {
            console.log('Superowner: using undefined as default for endDay');
            return undefined;
        }
        
        const urlDay = searchParams.get('endDay');
        console.log('Initializing endDay from URL:', urlDay);
        
        if (urlDay) {
            try {
                const date = new Date(urlDay);
                if (!isNaN(date.getTime())) {
                    console.log('Parsed endDay:', date);
                    return date;
                }
            } catch (e) {
                console.error('Error parsing endDay:', e);
            }
        }
        
        return undefined;
    });
    
    // Seller ID state - Initialize from URL
    const [sellerId, setSellerId] = useState<string | undefined>(() => {
        // For superowners, always use undefined without reading from URL
        if (isSuperOwner) {
            console.log('Superowner: using undefined as default for sellerId');
            return undefined;
        }
        
        const urlSellerId = searchParams.get('sellerId');
        console.log('Initializing sellerId from URL:', urlSellerId);
        
        if (urlSellerId) {
            return urlSellerId;
        }
        
        return undefined;
    });
    
    // add token to class object
    user?.token && AdminService.setToken(user.token)

    // Create wrapper functions that match Dispatch<SetStateAction> signature
    const handleSetTab = useCallback((value: SetStateAction<string | undefined>) => {
        if (!isUpdatingFromUrl.current) {
            setTab(value);
        }
    }, []);

    const handleSetCurrentDay = useCallback((value: SetStateAction<Date>) => {
        // Prevent state updates for superowners
        if (!isUpdatingFromUrl.current && !isSuperOwner) {
            setCurrentDay(value);
        }
    }, [isSuperOwner]);

    const handleSetEndDay = useCallback((value: SetStateAction<Date | undefined>) => {
        // Prevent state updates for superowners
        if (!isUpdatingFromUrl.current && !isSuperOwner) {
            setEndDay(value);
        }
    }, [isSuperOwner]);

    const handleSetSellerId = useCallback((value: SetStateAction<string | undefined>) => {
        // Prevent state updates for superowners
        if (!isUpdatingFromUrl.current && !isSuperOwner) {
            setSellerId(value);
        }
    }, [isSuperOwner]);

    // Single source of truth: Sync URL to state when URL changes
    useEffect(() => {
        if (!user) return;
        
        const currentParamsString = searchParams.toString();
        
        // Skip if this is the initial load (we already set state from URL in useState)
        if (!initialLoadDone.current) {
            initialLoadDone.current = true;
            prevSearchParamsString.current = currentParamsString;
            return;
        }
        
        // Skip if URL hasn't changed
        if (currentParamsString === prevSearchParamsString.current) {
            return;
        }
        
        console.log('URL changed, syncing to state:', currentParamsString);
        isUpdatingFromUrl.current = true;
        
        // Update tab from URL - always allow tab sync
        const urlTab = searchParams.get('tab');
        if (urlTab && urlTab !== tab) {
            setTab(urlTab);
        }
        
        // Only sync other parameters if NOT superowner
        if (!isSuperOwner) {
            // Update currentDay from URL
            const urlCurrentDay = searchParams.get('currentDay');
            if (urlCurrentDay) {
                try {
                    const newDate = new Date(urlCurrentDay);
                    if (!isNaN(newDate.getTime())) {
                        setCurrentDay(prev => {
                            if (prev.getTime() !== newDate.getTime()) {
                                return newDate;
                            }
                            return prev;
                        });
                    }
                } catch (e) {
                    console.error('Error parsing currentDay from URL:', e);
                }
            }
            
            // Update endDay from URL
            const urlEndDay = searchParams.get('endDay');
            if (urlEndDay) {
                try {
                    const newDate = new Date(urlEndDay);
                    if (!isNaN(newDate.getTime())) {
                        setEndDay(prev => {
                            if (!prev || prev.getTime() !== newDate.getTime()) {
                                return newDate;
                            }
                            return prev;
                        });
                    }
                } catch (e) {
                    console.error('Error parsing endDay from URL:', e);
                }
            } else {
                setEndDay(undefined);
            }
            
            // Update sellerId from URL
            const urlSellerId = searchParams.get('sellerId');
            if (urlSellerId) {
                setSellerId(urlSellerId);
            } else {
                setSellerId(undefined);
            }
        }
        
        prevSearchParamsString.current = currentParamsString;
        
        // Reset the flag after state updates are processed
        Promise.resolve().then(() => {
            isUpdatingFromUrl.current = false;
        });
        
    }, [searchParams, user, isSuperOwner, tab]);

    // Sync state to URL - debounced
    useEffect(() => {
        if (!user || !initialLoadDone.current || isUpdatingFromUrl.current) return;
        
        const timeoutId = setTimeout(() => {
            const params: Record<string, string> = { tab: tab || 'tours' };
            
            // Only add other parameters if NOT superowner
            if (!isSuperOwner) {
                // Add dates if they exist
                if (currentDay) {
                    params.currentDay = currentDay.toISOString();
                }
                
                if (endDay) {
                    params.endDay = endDay.toISOString();
                }
                
                // Add sellerId if it exists
                if (sellerId) {
                    params.sellerId = sellerId;
                }
            }
            
            const currentParams = searchParams.toString();
            const newParams = toQuery(params).replace('?', '');
            
            // Only update if params actually changed
            if (currentParams !== newParams) {
                console.log('Syncing state to URL:', newParams);
                router(`/admin${toQuery(params)}`);
            }
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [tab, currentDay, endDay, sellerId, router, user, searchParams, isSuperOwner]);

    // Memoize the context value
    const contextValue = useMemo<AdminContextType>(() => ({
        user, setUser,
        tab, setTab: handleSetTab,
        isCreateTourModalOpen, setIsCreateTourModalOpen,
        tourInfoId, setTourInfoId,
        isCreatePassengerModalOpen, setIsCreatePassengerModalOpen,
        isCreateAgencyModalOpen, setIsCreateAgencyModalOpen,
        isCreateSellerModalOpen, setIsCreateSellerModalOpen,
        isCreateAdminModalOpen, setIsCreateAdminModalOpen,
        isCreateDriverModalOpen, setIsCreateDriverModalOpen,
        isCreateGuiderModalOpen, setIsCreateGuiderModalOpen,
        passengerPlanId, setPassengerPlanId,
        currentDay, setCurrentDay: handleSetCurrentDay,
        endDay, setEndDay: handleSetEndDay,
        sellerId, setSellerId: handleSetSellerId,
        assignTripReservationId, setAssignTripReservationId,
        assignTripTourId, setAssignTripTourId,
        traspassTripReservationId, setTraspassTripReservationId,
        traspassTripTourId, setTraspassTripTourId,
        registerPassengersTripId, setRegisterPassengersTripId,
        registerCostsTripId, setRegisterCostsTripId,
        requirementsTripId, setRequirementsTripId,
        accountingSellerId, setAccountingSellerId,
        editTripId, setEditTripId,
        relationshipAgencyId, setRelationshipAgencyId,
        editReservationId, setEditReservationId,
        editPassengerData, setEditPassengerData,
        editTourData, setEditTourData,
        editDriverData, setEditDriverData,
        editGuiderData, setEditGuiderData,
        editSellerData, setEditSellerData,
        editAdminData, setEditAdminData,
        editAgencyData, setEditAgencyData,
        addPaymentData, setAddPaymentData,
        reservationDetails, setReservationDetails,
        viewPaymentAgencyId, setViewPaymentAgencyId,
        editTourInfoId, setEditTourInfoId,
        createBlogTourId, setCreateBlogTourId,
        isCreateZoneModalOpen, setIsCreateZoneModalOpen,
        editZoneId, setEditZoneId,
        groupDetails, setGroupDetails,
        listBlogsTourId, setListBlogsTourId,
        editBlog, setEditBlog,
        confirmDeleteTourId, setConfirmDeleteTourId,
        confirmDeleteAgencyId, setConfirmDeleteAgencyId,
        isCreateCarModalOpen, setIsCreateCarModalOpen,
        editCarData, setEditCarData,
        confirmDeleteCarId, setConfirmDeleteCarId,
        confirmDeleteTripId, setConfirmDeleteTripId,
        confirmDeleteReservationId, setConfirmDeleteReservationId,
        confirmUnassignReservationId, setConfirmUnassignReservationId,
        editPassengerReservationId, setEditPassengerReservationId,
        createPaymentReservationId, setCreatePaymentReservationId,
        isCreateCostModalOpen, setIsCreateCostModalOpen,
        editCostData, setEditCostData,
        confirmDeleteCostId, setConfirmDeleteCostId,
        editPaymentData, setEditPaymentData,
        confirmDeletePaymentId, setConfirmDeletePaymentId,
        isCreatePaymentReservationModalOpen, setIsCreatePaymentReservationModalOpen,
        viewPaymentsData, setViewPaymentsData,
        createPaymentResId, setCreatePaymentResId,
        reservationData, setReservationData,
        editPaymentReservationData, setEditPaymentReservationData,
        isCreateDailyCostModalOpen, setIsCreateDailyCostModalOpen,
        isListDailyCostsModalOpen, setIsListDailyCostsModalOpen,
        editDailyCostData, setEditDailyCostData,
        viewPaymentTraspassData, setViewPaymentTraspassData,
        confirmDeletePassengerId, setConfirmDeletePassengerId,
        createPaymentGroupId, setCreatePaymentGroupId,
        isCreateReTraspasoModalOpen, setIsCreateReTraspasoModalOpen,
        createPaymentReTraspasoData, setCreatePaymentReTraspasoData,
        createRevisionData, setCreateRevisionData,
        relationshipRefreshKey, setRelationshipRefreshKey,
    }), [
        user, tab, isCreateTourModalOpen, tourInfoId, isCreatePassengerModalOpen,
        isCreateAgencyModalOpen, isCreateSellerModalOpen, isCreateAdminModalOpen,
        isCreateDriverModalOpen, isCreateGuiderModalOpen, passengerPlanId,
        currentDay, endDay, sellerId, assignTripReservationId, assignTripTourId,
        traspassTripReservationId, traspassTripTourId, registerPassengersTripId,
        registerCostsTripId, requirementsTripId, accountingSellerId, editTripId,
        relationshipAgencyId, editReservationId, editPassengerData, editTourData,
        editDriverData, editGuiderData, editSellerData, editAdminData, editAgencyData,
        addPaymentData, reservationDetails, viewPaymentAgencyId, editTourInfoId,
        createBlogTourId, isCreateZoneModalOpen, editZoneId, groupDetails,
        listBlogsTourId, editBlog, confirmDeleteTourId, confirmDeleteAgencyId,
        isCreateCarModalOpen, editCarData, confirmDeleteCarId, confirmDeleteTripId,
        confirmDeleteReservationId, confirmUnassignReservationId, editPassengerReservationId,
        createPaymentReservationId, isCreateCostModalOpen, editCostData, confirmDeleteCostId,
        editPaymentData, confirmDeletePaymentId, isCreatePaymentReservationModalOpen,
        viewPaymentsData, createPaymentResId, reservationData, editPaymentReservationData,
        isCreateDailyCostModalOpen, isListDailyCostsModalOpen, editDailyCostData,
        viewPaymentTraspassData, confirmDeletePassengerId, createPaymentGroupId,
        isCreateReTraspasoModalOpen, createPaymentReTraspasoData, createRevisionData, relationshipRefreshKey,
        handleSetTab, handleSetCurrentDay, handleSetEndDay, handleSetSellerId
    ]);

    return <AdminProvider.Provider value={contextValue}>
        {children}
    </AdminProvider.Provider>;
}

export const useAdmin = () => {
    return useContext(AdminProvider)
}